import { useCallback, useEffect, useState } from "react";
import { GearSix, Plus, Trash, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";

const inputCls =
  "w-full bg-[#0B0C10] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-brand/50";

const EMPTY_T = { quote: "", name: "", context: "" };

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [savingLink, setSavingLink] = useState(false);
  const [reviewLink, setReviewLink] = useState("");
  const [configured, setConfigured] = useState(false);
  const [source, setSource] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [draft, setDraft] = useState(EMPTY_T);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/site-settings");
      setReviewLink(data.google_review_link || "");
      setConfigured(Boolean(data.review_link_configured));
      setSource(data.review_link_source);
      setTestimonials(Array.isArray(data.testimonials) ? data.testimonials : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveLink = async () => {
    setSavingLink(true);
    try {
      const { data } = await api.patch("/admin/site-settings", {
        google_review_link: reviewLink.trim(),
      });
      setConfigured(Boolean(data.review_link_configured));
      setSource(data.review_link_source);
      setReviewLink(data.google_review_link || "");
      toast.success(data.review_link_configured ? "Review link saved" : "Review link cleared");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingLink(false);
    }
  };

  const addTestimonial = async () => {
    if (draft.quote.trim().length < 12 || draft.name.trim().length < 2) {
      toast.error("Use the client's real words (12+ characters) and their real name.");
      return;
    }
    setAdding(true);
    try {
      const next = [...testimonials, { ...draft }];
      const { data } = await api.patch("/admin/site-settings", { testimonials: next });
      setTestimonials(data.testimonials || []);
      setDraft(EMPTY_T);
      toast.success("Testimonial published on the homepage");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAdding(false);
    }
  };

  const removeTestimonial = async (id) => {
    if (!window.confirm("Remove this client quote from the site?")) return;
    const previous = testimonials;
    const next = testimonials.filter((t) => t.id !== id);
    setTestimonials(next);
    try {
      const { data } = await api.patch("/admin/site-settings", { testimonials: next });
      setTestimonials(data.testimonials || []);
      toast.success("Quote removed", {
        action: {
          label: "Undo",
          onClick: async () => {
            const { data } = await api.patch("/admin/site-settings", { testimonials: previous });
            setTestimonials(data.testimonials || []);
          },
        },
      });
    } catch (e) {
      setTestimonials(previous);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) {
    return <p className="text-white/40 text-sm">Loading settings…</p>;
  }

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl text-white flex items-center gap-3">
          <GearSix size={28} weight="duotone" className="text-brand" /> Settings
        </h1>
        <p className="text-white/50 text-sm mt-1">
          Real reviews and real solds only. Nothing here is invented for you.
        </p>
      </div>

      <section className="bg-[#101116] border border-white/5 rounded-sm p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Star size={18} weight="duotone" className="text-brand" />
          <h2 className="text-lg text-white">Google review link</h2>
        </div>
        <p className="text-sm text-white/50 font-light leading-relaxed">
          In Google Business Profile: Ask for reviews → copy the share link. When you mark a
          CRM lead Closed, the site emails this link once. Also used as a P.S. on booking follow-ups.
        </p>
        <input
          data-testid="settings-review-link"
          className={inputCls}
          value={reviewLink}
          onChange={(e) => setReviewLink(e.target.value)}
          placeholder="https://g.page/r/…/review or https://search.google.com/local/writereview?placeid=…"
        />
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="settings-review-save"
            onClick={saveLink}
            disabled={savingLink}
            className="px-5 py-2.5 rounded-sm bg-brand text-black text-sm uppercase tracking-wider font-medium hover:bg-brand-hover disabled:opacity-60"
          >
            {savingLink ? "Saving…" : "Save review link"}
          </button>
          <span className="text-xs text-white/40" data-testid="settings-review-status">
            {configured
              ? `Ready (${source === "env" ? "Render env overrides dashboard" : "from this page"})`
              : "Not set — closed-deal review emails will not send"}
          </span>
        </div>
      </section>

      <section className="bg-[#101116] border border-white/5 rounded-sm p-6 space-y-4">
        <h2 className="text-lg text-white">Client testimonials</h2>
        <p className="text-sm text-white/50 font-light leading-relaxed">
          Only quotes a real client said. The homepage hides this section until at least one exists.
        </p>

        {testimonials.length === 0 ? (
          <p className="text-white/40 text-sm" data-testid="settings-testimonials-empty">
            No testimonials yet — add one after a real closing.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="settings-testimonials-list">
            {testimonials.map((t) => (
              <li key={t.id} className="border border-white/10 rounded-sm p-4">
                <p className="text-white/80 text-sm font-light leading-relaxed">“{t.quote}”</p>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <p className="text-xs text-white/40 uppercase tracking-wider">
                    {t.name}{t.context ? ` · ${t.context}` : ""}
                  </p>
                  <button
                    type="button"
                    data-testid={`settings-testimonial-remove-${t.id}`}
                    onClick={() => removeTestimonial(t.id)}
                    className="text-xs text-white/40 hover:text-red-400 flex items-center gap-1"
                  >
                    <Trash size={13} /> Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-3 pt-2 border-t border-white/5">
          <label className="lux-label">Their words</label>
          <textarea
            data-testid="settings-testimonial-quote"
            rows={3}
            className={`${inputCls} resize-none`}
            value={draft.quote}
            onChange={(e) => setDraft({ ...draft, quote: e.target.value })}
            placeholder="Paste what they actually said…"
          />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="lux-label">Name</label>
              <input
                data-testid="settings-testimonial-name"
                className={inputCls}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="First name + last initial"
              />
            </div>
            <div>
              <label className="lux-label">Context (optional)</label>
              <input
                data-testid="settings-testimonial-context"
                className={inputCls}
                value={draft.context}
                onChange={(e) => setDraft({ ...draft, context: e.target.value })}
                placeholder="Bought in Mott Haven"
              />
            </div>
          </div>
          <button
            type="button"
            data-testid="settings-testimonial-add"
            onClick={addTestimonial}
            disabled={adding}
            className="flex items-center gap-2 px-5 py-2.5 rounded-sm border border-white/15 text-sm text-white/80 hover:text-white hover:border-brand/40 disabled:opacity-60"
          >
            <Plus size={14} weight="bold" /> {adding ? "Adding…" : "Add real testimonial"}
          </button>
        </div>
      </section>
    </div>
  );
}
